import { SearchFocusTarget, User } from "@/App";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Plus, Edit, Clock } from "lucide-react";
import { AddWebsitePanel } from "./website-management-panels/AddWebsitePanel";
import { ManageWebsitesPanel } from "./website-management-panels/ManageWebsitesPanel";
import { ManageSuggestionsPanel } from "./website-management-panels/ManageSuggestionsPanel";
import type { WebsiteSuggestion } from "@/components/website-suggest/types";
import type { WebsiteStatus } from "@/services/website.service";

interface WebsiteManagementProps {
  navigate: (page: string) => void;
  currentUser: User | null;
  onLogout: () => void;
  suggestions: WebsiteSuggestion[];
  onApproveSuggestion: (
    id: string,
    modified?: Partial<WebsiteSuggestion>
  ) => void;
  onRejectSuggestion: (id: string) => void;
  onSaveSuggestion: (
    id: string,
    modified: Partial<WebsiteSuggestion>
  ) => Promise<void>;
  onAddWebsite: (
    website: Omit<Website, "id" | "addedAt" | "addedBy">
  ) => Promise<void>;
  onEditWebsite: (id: string, updates: Partial<Website>) => void;
  onDeleteWebsite: (id: string) => void;
  onNavigateToSearchPage: (target: SearchFocusTarget) => void;
}

export interface Website {
  id: string;
  title: string;
  url: string;
  description: string;
  category1Id?: string | null;
  category1Name?: string;
  categoryPathIds?: string[];
  categoryPathNames?: string[];
  addedBy?: string;
  addedAt: string;
  region?: string;
  regionId?: string | null;
  regionPathIds?: string[];
  regionPathNames?: string[];
  zipCode?: string | null;
  address?: string | null;
  status?: WebsiteStatus;
}

export function WebsiteManagement({
  navigate,
  currentUser,
  onLogout,
  suggestions,
  onApproveSuggestion,
  onRejectSuggestion,
  onSaveSuggestion,
  onAddWebsite,
  onEditWebsite,
  onDeleteWebsite,
  onNavigateToSearchPage,
}: WebsiteManagementProps) {
  const { t } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const tabTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 82%, transparent)",
  } as const;
  const getTabFromUrl = useCallback(() => {
    if (typeof window === "undefined") return "add";
    const value = new URLSearchParams(window.location.search).get("tab");
    if (value === "manage" || value === "suggestions") return value;
    return "add";
  }, []);
  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  const scrollToTop = useCallback(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);
  useEffect(() => {
    scrollToTop();
  }, [scrollToTop]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handlePopState = () => {
      setActiveTab(getTabFromUrl());
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [getTabFromUrl]);
  const pendingSuggestions = suggestions.filter((s) => s.status === "pending");
  const setTabInUrl = useCallback((value: string) => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (value === "add") {
      url.searchParams.delete("tab");
    } else {
      url.searchParams.set("tab", value);
    }
    window.history.replaceState(window.history.state, "", url.toString());
  }, []);
  const handleTabChange = useCallback(
    (value: string) => {
      setActiveTab(value);
      setTabInUrl(value);
    },
    [setTabInUrl]
  );
  const tabValue = useMemo(() => activeTab || "add", [activeTab]);

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ backgroundColor: "var(--background)" }}
    >
      <Header
        navigate={navigate}
        currentUser={currentUser}
        onLogout={onLogout}
        currentPage="manage-websites"
        samePageHandlers={{ "manage-websites": scrollToTop }}
        onNavigateToSearchPage={onNavigateToSearchPage}
      />

      <main className="flex-1 py-8">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h2 className="text-primary-heading mb-2">
              {t("websiteManagement.title")}
            </h2>
            <p className="text-secondary">{t("websiteManagement.subtitle")}</p>
          </div>

          <Tabs value={tabValue} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid w-full max-w-3xl grid-cols-3 tabs-list-standard h-auto gap-2 p-2 mb-8">
              <TabsTrigger
                value="add"
                className="gap-1 md:gap-2 text-xs md:text-sm tabs-trigger-standard"
                style={tabTextStyle}
              >
                <Plus className="w-3 h-3 md:w-4 md:h-4" style={iconStyle} />
                <span className="hidden sm:inline">
                  {t("websiteManagement.tabAdd")}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="manage"
                className="gap-1 md:gap-2 text-xs md:text-sm tabs-trigger-standard"
                style={tabTextStyle}
              >
                <Edit className="w-3 h-3 md:w-4 md:h-4" style={iconStyle} />
                <span className="hidden sm:inline">
                  {t("websiteManagement.tabManage")}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="suggestions"
                className="gap-1 md:gap-2 text-xs md:text-sm tabs-trigger-standard"
                style={tabTextStyle}
              >
                <Clock className="w-3 h-3 md:w-4 md:h-4" style={iconStyle} />
                <span className="hidden sm:inline">
                  {t("websiteManagement.tabSuggestions")}
                </span>{" "}
                ({pendingSuggestions.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="add">
              <AddWebsitePanel
                onAddWebsite={onAddWebsite}
                navigate={navigate}
              />
            </TabsContent>

            <TabsContent value="manage">
              <ManageWebsitesPanel
                onEditWebsite={onEditWebsite}
                onDeleteWebsite={onDeleteWebsite}
              />
            </TabsContent>

            <TabsContent value="suggestions">
              <ManageSuggestionsPanel
                suggestions={suggestions}
                onApproveSuggestion={onApproveSuggestion}
                onRejectSuggestion={onRejectSuggestion}
                onSaveSuggestion={onSaveSuggestion}
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer navigate={navigate} />
    </div>
  );
}
